import React from 'react'
import KaTeXRenderer from './KaTeXRenderer'

interface SimpleMarkdownRendererProps {
  content: string
  className?: string
}

const SimpleMarkdownRenderer: React.FC<SimpleMarkdownRendererProps> = ({ content, className = '' }) => {
  if (!content) return null

  // Xử lý nội dung đơn giản
  const processContent = (text: string): React.ReactNode => {
    // Tách nội dung thành các phần (text và LaTeX)
    const parts = text.split(/(\$\$[^$]+\$\$|\$[^$]+\$)/g)
    
    return parts.map((part, index) => {
      // Nếu là LaTeX display ($$...$$)
      if (part.startsWith('$$') && part.endsWith('$$')) {
        const latexContent = part.slice(2, -2)
        return (
          <div key={index} className="my-4 text-center">
            <KaTeXRenderer 
              content={latexContent} 
              className="block"
            />
          </div>
        )
      }
      // Nếu là LaTeX inline ($...$)
      else if (part.startsWith('$') && part.endsWith('$')) {
        const latexContent = part.slice(1, -1)
        return (
          <KaTeXRenderer 
            key={index} 
            content={latexContent} 
            className="inline"
          />
        )
      }
      
      // Nếu là text thường, xử lý đơn giản
      return processText(part, index)
    })
  }

  // Xử lý text đơn giản
  const processText = (text: string, key: number): React.ReactNode => {
    if (!text.trim()) return null

    // Xử lý xuống dòng
    const lines = text.split('\n')
    
    return lines.map((line, lineIndex) => {
      if (!line.trim()) {
        return <br key={`${key}-${lineIndex}`} />
      }

      // Xử lý ### headings
      if (line.startsWith('### ')) {
        const headingText = line.slice(4)
        return (
          <h3 key={`${key}-${lineIndex}`} className="text-lg font-semibold text-gray-800 mt-4 mb-2">
            {processInlineText(headingText)}
          </h3>
        )
      }

      // Xử lý ## headings
      if (line.startsWith('## ')) {
        const headingText = line.slice(3)
        return (
          <h2 key={`${key}-${lineIndex}`} className="text-xl font-semibold text-gray-800 mt-4 mb-2">
            {processInlineText(headingText)}
          </h2>
        )
      }

      // Xử lý # headings
      if (line.startsWith('# ')) {
        const headingText = line.slice(2)
        return (
          <h1 key={`${key}-${lineIndex}`} className="text-2xl font-bold text-gray-800 mt-4 mb-2">
            {processInlineText(headingText)}
          </h1>
        )
      }

      // Xử lý bullet points (hỗ trợ cả - và •)
      if (line.startsWith('- ') || line.startsWith('• ')) {
        const bulletText = line.slice(2)
        return (
          <div key={`${key}-${lineIndex}`} className="flex items-start mb-1">
            <span className="mr-2 text-gray-600">•</span>
            <span className="text-gray-800">
              {processInlineText(bulletText)}
            </span>
          </div>
        )
      }

      // Xử lý text thường
      return (
        <div key={`${key}-${lineIndex}`} className="mb-2 text-gray-800">
          {processInlineText(line)}
        </div>
      )
    })
  }

  // Xử lý inline text (bold, italic, ký tự toán học)
  const processInlineText = (text: string): React.ReactNode => {
    // Xử lý **bold**
    const boldParts = text.split(/(\*\*[^*]+\*\*)/g)
    
    const lineContent = boldParts.map((part, partIndex) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        const boldText = part.slice(2, -2)
        return <strong key={`bold-${partIndex}`}>{boldText}</strong>
      }
      return part
    })

    // Xử lý *italic*
    const italicParts = lineContent.map((part, partIndex) => {
      if (typeof part === 'string') {
        const italicSplit = part.split(/(\*[^*]+\*)/g)
        return italicSplit.map((italicPart, italicIndex) => {
          if (italicPart.startsWith('*') && italicPart.endsWith('*')) {
            const italicText = italicPart.slice(1, -1)
            return <em key={`italic-${partIndex}-${italicIndex}`}>{italicText}</em>
          }
          return italicPart
        })
      }
      return part
    })

    return italicParts
  }

  return (
    <div className={className}>
      {processContent(content)}
    </div>
  )
}

export default SimpleMarkdownRenderer
